import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Sparkles } from 'lucide-react';

const JollyDialog = ({
  jollyMatch,
  clubs,
  isSuperAdmin,
  isItalian,
  jollyDate,
  setJollyDate,
  jollyTime,
  setJollyTime,
  jollyClubId,
  setJollyClubId,
  jollyInfo,
  jollyLoading,
  onSubmit,
  onClose,
}) => {
  const getClub = (clubId) => clubs.find((c) => c.club_id === clubId);

  return (
    <Dialog open={!!jollyMatch} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-[#0a0d15] border border-[#FFEA00]/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg uppercase tracking-wider flex items-center gap-2">
            <Sparkles size={20} className="text-[#FFEA00]" />
            {isItalian ? 'Richiesta Jolly' : 'Jolly Request'}
          </DialogTitle>
        </DialogHeader>
        {jollyMatch && (
          <div className="space-y-4">
            <div className="p-3 bg-[#050508] border border-white/10 rounded text-sm">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{isItalian ? 'Partita' : 'Match'}</p>
              <p className="text-white font-medium">
                {getClub(jollyMatch.home_club_id)?.name || '?'} vs {getClub(jollyMatch.away_club_id)?.name || '?'}
              </p>
              {jollyMatch.match_date && (
                <p className="text-xs text-slate-500 mt-1">
                  {isItalian ? 'Data attuale:' : 'Current date:'}{' '}
                  {new Date(jollyMatch.match_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {' '}
                  {new Date(jollyMatch.match_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>

            <div className="space-y-3">
              {isSuperAdmin && jollyMatch && (
                <div>
                  <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
                    {isItalian ? 'Jolly per quale squadra?' : 'Jolly for which club?'}
                  </Label>
                  <select
                    value={jollyClubId}
                    onChange={(e) => setJollyClubId(e.target.value)}
                    className="w-full bg-[#050508] border border-white/10 text-white px-3 py-2 text-sm rounded"
                    data-testid="jolly-club-select"
                  >
                    <option value="">{isItalian ? '-- Seleziona squadra --' : '-- Select club --'}</option>
                    <option value={jollyMatch.home_club_id}>
                      {getClub(jollyMatch.home_club_id)?.name || jollyMatch.home_club_id} (Casa)
                    </option>
                    <option value={jollyMatch.away_club_id}>
                      {getClub(jollyMatch.away_club_id)?.name || jollyMatch.away_club_id} (Ospite)
                    </option>
                  </select>
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
                  {isItalian ? 'Nuova Data' : 'New Date'}
                </Label>
                <Input
                  type="date"
                  value={jollyDate}
                  onChange={(e) => setJollyDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="jolly-date-input"
                  className="bg-[#050508] border-white/10"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
                  {isItalian ? 'Orario' : 'Time'}
                </Label>
                <Input
                  type="time"
                  value={jollyTime}
                  onChange={(e) => setJollyTime(e.target.value)}
                  data-testid="jolly-time-input"
                  className="bg-[#050508] border-white/10"
                />
              </div>
            </div>

            <p className="text-xs text-slate-500">
              {jollyInfo ? (
                jollyInfo.jolly_free_remaining > 0
                  ? (isItalian 
                      ? `Hai ancora ${jollyInfo.jolly_free_remaining} Jolly gratuiti su ${jollyInfo.jolly_free}. La richiesta verrà inviata all'amministrazione per approvazione.`
                      : `You have ${jollyInfo.jolly_free_remaining} free Jolly left out of ${jollyInfo.jolly_free}. The request will be sent to administration for approval.`)
                  : (isItalian 
                      ? `Hai esaurito i ${jollyInfo.jolly_free} Jolly gratuiti. Il prossimo costa 100.000 coins del club.`
                      : `You used all ${jollyInfo.jolly_free} free Jolly. The next one costs 100,000 club coins.`)
              ) : (isItalian 
                ? 'La richiesta verrà inviata all\'amministrazione per approvazione.'
                : 'The request will be sent to administration for approval.'
              )}
            </p>

            {jollyInfo && jollyInfo.next_costs > 0 && (
              <div className={`p-3 rounded border text-sm ${jollyInfo.can_afford ? 'bg-[#FFEA00]/10 border-[#FFEA00]/30' : 'bg-[#FF1744]/10 border-[#FF1744]/30'}`}>
                <p className={`font-bold ${jollyInfo.can_afford ? 'text-[#FFEA00]' : 'text-[#FF1744]'}`}>
                  {isItalian ? `Costo: ${jollyInfo.next_costs.toLocaleString()} coins` : `Cost: ${jollyInfo.next_costs.toLocaleString()} coins`}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {isItalian 
                    ? `Il tuo club ha ${(jollyInfo.club_coins || 0).toLocaleString()} coins`
                    : `Your club has ${(jollyInfo.club_coins || 0).toLocaleString()} coins`}
                </p>
                {!jollyInfo.can_afford && (
                  <p className="text-xs text-[#FF1744] mt-1 font-bold">
                    {isItalian ? 'Coins insufficienti!' : 'Not enough coins!'}
                  </p>
                )}
              </div>
            )}

            <Button
              className="w-full bg-[#FFEA00]/20 border border-[#FFEA00]/40 text-[#FFEA00] hover:bg-[#FFEA00]/30 font-bold uppercase tracking-wider"
              onClick={onSubmit}
              disabled={jollyLoading || !jollyDate || (isSuperAdmin && !jollyClubId) || (jollyInfo && jollyInfo.next_costs > 0 && !jollyInfo.can_afford)}
              data-testid="jolly-submit-btn"
            >
              {jollyLoading ? (
                <span className="flex items-center gap-2 justify-center">
                  <span className="w-4 h-4 border-2 border-[#FFEA00]/30 border-t-[#FFEA00] rounded-full animate-spin" />
                  {isItalian ? 'Invio...' : 'Sending...'}
                </span>
              ) : (
                <span className="flex items-center gap-2 justify-center">
                  <Sparkles size={16} />
                  {jollyInfo && jollyInfo.next_costs > 0
                    ? (isItalian ? `Acquista Jolly (${jollyInfo.next_costs.toLocaleString()} coins)` : `Buy Jolly (${jollyInfo.next_costs.toLocaleString()} coins)`)
                    : (isItalian ? 'Invia Richiesta Jolly' : 'Send Jolly Request')
                  }
                </span>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JollyDialog;
